import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'

import { retryRequestOnError } from '../common/helpers/helpers'
import { useQuery } from '../common/useQuery'
import { getCredentials } from '../store/middleware/credential.action'
import { AppDispatch, AppState } from '../store'
import settingsService from '../service/settings-service'
import textContentsService from '../service/TextContentsService'
import { SET_INITIAL_TEXT_CONTENTS } from '../store/actions/text-contents-action'
import { SET_ANALYTICS_EVENT_TYPES_NAMES, SET_LEN_CONFIG } from '../store/actions/len-config-actions'
import { STORAGEKEYS } from '../common/keys'
import { ApplicationStates } from '../common/application.state'
import { invokeLoadingWithText } from '../common/invokeLoading'
import useTextContents from './useTextContents'
import analyticsService from '../service/analytics.service'
import { APP_INIT_TYPES, IUseAppLogicReturnModel } from './HooksProps'
import { SET_SINGLE_FIELD_VALUE } from '../store/actions/form-actions'
import { FormNames } from '../models/form/IFormDefinition'
import { BasicInfoFieldsNames } from '../formDefinitions/BasicInfo/BasicInfoFields'
import { SET_CURRENT_USER_OFFER_PARAMS } from '../store/actions/offert-action'
import { SessionStorage } from '../common/cookie-storage'
import { ICalculateLoanParams, loanInitService } from '../service/LoanInitService'
import { SET_LOAN_CALCULATE_PARAMS } from '../store/actions/loan-calculator-actions'
import { ApiError } from '../common/http-client'
import { APP_CONSENTS_FETCHED } from '../store/actions/consent-action'
import { SET_USER_EMAIL_FROM_HUB } from '../store/actions/application-actions'
import { enablePredefinedPeriodsLogicForAffiliatePartner } from '../pages/AffiliatePage/affiliatePartnersData'
import Cookies from 'universal-cookie'


/**
 * Hook obsługujący inicjalizację aplikacji
 * - Pobiera access token z Backendu
 * - Pobiera konfigurację aplikacji oraz teksty z bazy Mongo
 * - Obsługuje requesty analityczne 
*/
const useAppInitLogic = (type: APP_INIT_TYPES): IUseAppLogicReturnModel  => {
   const dispatch = useDispatch<AppDispatch>()
   const textContent = useTextContents()
   const query = useQuery()
   const cookie = new Cookies()
   const sessionStorage = new SessionStorage()
   const ACTION_SOURCE_COOKIE_KEY = "action_source"

   const detectActionSource = (): "website" | "business_messaging" => {
      const explicitActionSource = query.get("action_source")?.toLowerCase()
      if (explicitActionSource === "website" || explicitActionSource === "business_messaging") {
         return explicitActionSource
      }

      const sourceHints = [
         query.get("source"),
         query.get("utm_source"),
         query.get("utm_medium"),
         typeof document !== "undefined" ? document.referrer : null
      ]
         .filter((v): v is string => !!v)
         .map(v => v.toLowerCase())
         .join(" ")

      return /(messenger|instagram|ig|whatsapp|wa\.me|m\.me)/.test(sourceHints)
         ? "business_messaging"
         : "website"
   }

   const persistActionSource = () => {
      sessionStorage.addCookie(ACTION_SOURCE_COOKIE_KEY, detectActionSource())
   }

   /**
      * Funkcja pomocnicza - logowanie do backendu i pobieranie access tokena - Wywołanie funkcji może zostać powtórzone w przypadku niepowodzenia
   */
   const getAccessToken = async(): Promise<number | undefined> => {
      try {
         persistActionSource()
         const appId = query.get("appId") as string
         // source zapisujemy wyłącznie przy wejściu na /start, nie przy powrocie /return
         const startLinkVisit = type === APP_INIT_TYPES.START_PAGE
            ? { isStartLink: true, source: query.get("source") }
            : undefined
         const getCredentialActionResponse = await retryRequestOnError(() => dispatch(getCredentials(appId, startLinkVisit)), 2) // Dwa powtórzenia w przypadku błędu

         // W przypadku powrotu z linku, pobieramy i ustawiamy email oraz wniskowaną kwotę pożyczki. Wartości potrzebne na dalszym etapie dla GTM (Google Tag Manager )
         switch(type) {
            case APP_INIT_TYPES.COMEBACK_PAGE: {
               dispatch({
                  type: SET_USER_EMAIL_FROM_HUB,
                  payload: getCredentialActionResponse.userEmail
               })


               //!!! IMPORTANT - LOGIKA BIZNESOWA - jeśli NIE DOSTAJEMY instalmentAmount oznacza to, że użytkownik jest PRZED ETAPEM WYBORU OFERTY.
               //!!! W takim przypadku dokonujemy wstępnego przeliczenia oferty na podstawie requestedAmount oraz instalments otrzymanych z odpowiedzi z /token
               //!!! JEŚLI UĄŻYTKOWNIK POSIADA JUŻ WYBRANĄ OFERTĘ (czyli instalmentAmount jest UZUPEŁNIONE), to zapisujemy te wartości do stanu aplikacji i nie przeliczamy oferty ponownie
               if(getCredentialActionResponse.currentInstalmentAmount === null){
               const calculateLoanParams: ICalculateLoanParams = {
                     instalments: getCredentialActionResponse.currentInstalments,
                     requestedAmount: getCredentialActionResponse.currentRequestedAmount
                  }
                  
                  const initialLoanOffert = await loanInitService.calculateLoanOffert(calculateLoanParams)

                  dispatch({
                     type: SET_LOAN_CALCULATE_PARAMS,
                     payload: initialLoanOffert
                  })  
                  
               }
               else {             
                  dispatch({
                     type: SET_CURRENT_USER_OFFER_PARAMS,
                     payload: {
                        requestedAmount: getCredentialActionResponse.currentRequestedAmount,
                        instalments: getCredentialActionResponse.currentInstalments,
                        instalmentsAmount: getCredentialActionResponse.currentInstalmentAmount
                     }
                  })
               }

               break;
            }
            case APP_INIT_TYPES.START_PAGE: {
               dispatch({
                  type: SET_USER_EMAIL_FROM_HUB,
                  payload: getCredentialActionResponse.userEmail
               })

               break;
            }

            default: {
               console.error("Nieobsługiwana wartość 'type' w useAppInitLogic: ", type)
               break;
            }
         }

         /**
          * [ZAŁOŻENIE BIZNESOWE 82880] - MECHANIZM PREDEFINIOWANYCH OKRESÓW POŻYCZKI
          * Dostajemy z backendu informację o tym czy dany użytkownik jest z afiliacji czy nie. 
          * Jeśli przyszedł z afiliacji dla której opcja predefiniowanych rat jest włączona, wykonujemy kod włączający logikę predefiniowanych okresów  
         */


         const affiliatePartnerIdFromCookie = cookie.get(STORAGEKEYS.FORM_AFFILIATE_DATA_COOKIE)?.partnerId ?? null
         const currentAffiliatePartnerIdToCheck = getCredentialActionResponse.affiliatePartnerId ?? affiliatePartnerIdFromCookie ?? null
         enablePredefinedPeriodsLogicForAffiliatePartner(dispatch, currentAffiliatePartnerIdToCheck)

         return getCredentialActionResponse.workflowId
      } 
      catch (e: any) {
         if (e instanceof ApiError) {
            e.message = `Błąd podczas inicjalizacji tokena: ${ e.message }`
            throw e
         }
      }
   }


   /**
      * Funkcja pomocnicza - pobiera konfigurację aplikacji z backendu i zapisuje ją w stanie aplikacji
   */
   const getAppConfiguration = async() => {
      try {
         const [lenConfig, textContents] = await Promise.all([
            retryRequestOnError(() => settingsService.getLenConfig(), 2), // Dwa powtórzenia w przypadku błędu
            retryRequestOnError(() => textContentsService.getTextContents(), 2) // Dwa powtórzenia w przypadku błędu
         ])
  
         dispatch({
            type: SET_INITIAL_TEXT_CONTENTS,
            payload: textContents.data
         })
  
         dispatch({
            type: SET_LEN_CONFIG,
            payload: {
               ...lenConfig.data,
               globalConfig: {
                  ...lenConfig.data.globalConfig,
                  invalidNipNumbers: lenConfig.data.globalConfig.invalidNipNumbers ?? [],
                  invalidPeselNumbers: lenConfig.data.globalConfig.invalidPeselNumbers ?? [],
               },
            }
         })

         if(type === APP_INIT_TYPES.START_PAGE && !isEmpty(textContents.data)){ // Klient przychodzący od Integratora, ma już uzupełnione dane
            sessionStorage.changeItemValue(STORAGEKEYS.CURRENT_PAGE_STEP, ApplicationStates.FORM_LAST_STEP.toString());
            dispatch(invokeLoadingWithText(textContent('loaders.default')))
         }
      }
      catch (e: any) {
         if (e instanceof ApiError) {
            e.message = `Błąd podczas pobierania konfiguracji aplikacji: ${e.message}`
            throw e
         }
      }
   }


   /**
      * Funkcja pomocnicza - pobiera konfigurację aplikacji z backendu i zapisuje ją w stanie aplikacji
   */
   const getAppConsents = async() => {
      try {
         const appConsents = await retryRequestOnError(() => settingsService.getConsents(), 2)

         dispatch({
            type: APP_CONSENTS_FETCHED,
            payload: appConsents
         })
      }
      catch (e: any) {
         if (e instanceof ApiError) {
            e.message = `Błąd podczas pobierania zgód aplikacji: ${e.message}`
            throw e
         }
      }
   }

   /**
      * Funkcja pomocnicza do obsługi requestów analitycznych - WYMAGA OBECNOŚCI ACCESS TOKENA, stąd wykonywana jest jako ostatnia.
   */
   const handleAnalyticsRequests = async() => {
      try {
         const comebackPageClientSource = query.get("source") ?? "default" // Customowy parametr mówiący o tym źródle z którego użytkownik wszedł w link (email, sms, default - od integratora)
         const analyticsEventTypesNames = await retryRequestOnError(() => analyticsService.getAnalyticsEventTypes(), 2)

         if(analyticsEventTypesNames !== undefined) {
            dispatch({
               type: SET_ANALYTICS_EVENT_TYPES_NAMES,
               payload: analyticsEventTypesNames,
            })

            if(type === APP_INIT_TYPES.COMEBACK_PAGE) { // Jeśli comebackPage, wysyłamy event analityczny, jeśli startPage nic nie robimy
               await analyticsService.createSingleAnalyticEvent(analyticsEventTypesNames.EntryFromReturnUrl, comebackPageClientSource)
            }
         }
         else {
            console.error("Nie udało się pobrać nazw eventów analitycznych")
         }
      }
      catch (e: any) {
         console.error("Błąd podczas wykonywania requestów analitycznych: ", e)
      }
   }


   return {
      getAccessToken,
      getAppConfiguration,
      getAppConsents,
      handleAnalyticsRequests,
   }
}


export default useAppInitLogic